import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Radar, Settings2, Play, Pause, Star } from "lucide-react";
import {
  approveProposal,
  getAnalysis,
  getEvents,
  getRisk,
  getScanner,
  getSettings,
  getSnapshots,
  listMyRuns,
  listProposals,
  patchScanner,
  patchSettings,
  rejectProposal,
  scanNow,
  toastApiError,
} from "@/lib/api";
import { usePortfolio } from "@/lib/portfolio-context";
import { fxMarketState } from "@/lib/fxHours";
import TickerBar from "@/pages/desk/TickerBar";
import AgentRoster from "@/pages/desk/AgentRoster";
import RiskGauges from "@/pages/desk/RiskGauges";
import ApprovalQueue from "@/pages/desk/ApprovalQueue";
import DeskFeed from "@/pages/desk/DeskFeed";
import OmsPanel from "@/pages/desk/OmsPanel";
import DeskSettingsDialog from "@/pages/desk/DeskSettingsDialog";
import ChartPro from "@/pages/desk/ChartPro";
import ScannerCard from "@/pages/desk/ScannerCard";
import WatchlistPanel from "@/pages/desk/WatchlistPanel";
import QuickTradeDialog from "@/pages/desk/QuickTradeDialog";

const POLL_MS = 3000;
const DEFAULT_CHART_SYMBOL = "AAPL";
const AUTOPILOT_CONFIRM_KEY = "numirock_autopilot_confirmed_v1";

function formatSecondsShort(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${String(r).padStart(2, "0")}s` : `${m}m`;
}

/**
 * Autopilot control — the SINGLE on/off switch for the desk.
 *
 * On start: PATCH scanner {enabled:true}, PATCH desk settings {auto_mode:true},
 * then trigger an immediate scan-now cycle. On pause: flips both back off
 * (pending proposals stay for manual decision).
 */
function AutopilotControl({
  portfolioId,
  portfolioType,
  scannerStatus,
  autoMode,
  onChange,
  // Test/screenshot hook: force the FX-hours "now" to a fixed instant so
  // the paused-chip variant can be captured without waiting for the real
  // weekend clock. Pass a Date; undefined means "use system time".
  nowOverride,
}) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const enabled = !!(scannerStatus?.config?.enabled && autoMode);
  const isFxEngine =
    (portfolioType || "").toLowerCase() === "oanda"
    || (portfolioType || "").toLowerCase() === "capital";
  const fx = isFxEngine ? fxMarketState(nowOverride) : null;
  const fxPaused = !!fx && !fx.open && enabled;

  const doStart = async () => {
    if (!portfolioId) return;
    setBusy(true);
    try {
      await patchScanner(portfolioId, { enabled: true });
      await patchSettings(portfolioId, { auto_mode: true });
      // Fire an immediate scan cycle (best-effort; ignore 429 if within 60s cooldown)
      try {
        await scanNow(portfolioId);
      } catch {
        /* silent — cooldown or existing cycle */
      }
      toast.success("Autopilot engaged.");
      onChange?.();
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const doStop = async () => {
    if (!portfolioId) return;
    setBusy(true);
    try {
      await patchScanner(portfolioId, { enabled: false });
      await patchSettings(portfolioId, { auto_mode: false });
      toast.success("Autopilot paused.");
      onChange?.();
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const onClick = () => {
    if (enabled) {
      doStop();
      return;
    }
    let confirmed = false;
    try {
      confirmed = window.localStorage.getItem(AUTOPILOT_CONFIRM_KEY) === "1";
    } catch {
      /* private mode / SSR — treat as not confirmed */
    }
    if (!confirmed) {
      setConfirmOpen(true);
    } else {
      doStart();
    }
  };

  const confirmAndStart = () => {
    try {
      window.localStorage.setItem(AUTOPILOT_CONFIRM_KEY, "1");
    } catch {
      /* ignore */
    }
    setConfirmOpen(false);
    doStart();
  };

  const nextEta =
    scannerStatus?.next_scan_in_seconds != null
      ? formatSecondsShort(scannerStatus.next_scan_in_seconds)
      : "…";

  return (
    <>
      <button
        data-testid={enabled ? "autopilot-pause-btn" : "autopilot-play-btn"}
        onClick={onClick}
        disabled={!portfolioId || busy}
        className={`group flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-40 ${
          enabled
            ? "autopilot-active border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/15 text-[var(--aurion-gold)]"
            : "border-[var(--aurion-gold)]/50 text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/10"
        }`}
      >
        {enabled ? (
          <Pause className="h-3.5 w-3.5 fill-current" />
        ) : (
          <Play className="h-3.5 w-3.5 fill-current" />
        )}
        <span>{enabled ? "AUTO" : "PLAY"}</span>
        {enabled ? (
          <span
            data-testid={fxPaused ? "autopilot-paused-fx" : "autopilot-eta"}
            className="ml-1 text-[9px] tracking-widest text-[var(--aurion-gold)]/80"
          >
            {fxPaused
              ? `· paused until ${fx.label.replace(/^opens\s+/i, "")}`
              : `· next scan ${nextEta}`}
          </span>
        ) : null}
      </button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="autopilot-confirm-dialog"
          className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-lg tracking-widest text-[var(--aurion-gold)]">
              Engage Autopilot?
            </DialogTitle>
            <DialogDescription className="text-[12px] leading-relaxed text-[var(--aurion-muted-strong)]">
              NUMIROCK will scan markets, run agents, and execute paper trades
              automatically. Guardrails: conviction ≥ threshold, daily caps,
              drawdown kill-switch.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 flex gap-2">
            <button
              data-testid="autopilot-cancel"
              onClick={() => setConfirmOpen(false)}
              className="border border-[var(--aurion-border)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
            >
              Cancel
            </button>
            <button
              data-testid="autopilot-confirm-start"
              onClick={confirmAndStart}
              className="aurion-btn-gold px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest"
            >
              Start
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function DeskPage() {
  const nav = useNavigate();
  const { portfolioId: routePid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { portfolios, selectedId: activePid, selectPortfolio } = usePortfolio();
  const [settings, setSettings] = useState(null);
  const [risk, setRisk] = useState(null);
  const [events, setEvents] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [chartSymbol, setChartSymbol] = useState(
    searchParams.get("symbol") || DEFAULT_CHART_SYMBOL,
  );
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [speakingRole, setSpeakingRole] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [omsRefreshKey, setOmsRefreshKey] = useState(0);
  const [scannerStatus, setScannerStatus] = useState(null);
  const [rightTab, setRightTab] = useState("scanner");
  const [tradeIntent, setTradeIntent] = useState(null);

  // Screenshot / test hook: `?fx_now=<ISO>` freezes the FX-hours clock so
  // the AutopilotControl paused-chip variant can be captured on demand
  // without waiting for the real weekend. No effect on backend; the URL
  // param is only read here.
  const fxNowOverride = useMemo(() => {
    const raw = searchParams.get("fx_now");
    if (!raw) return undefined;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [searchParams]);

  const speakingTimer = useRef(null);
  const runPoll = useRef(null);
  const deskPoll = useRef(null);
  // Single-writer guard for the URL <-> context sync so a strip-picker
  // change and its follow-up URL nav don't chase each other's tail. Both
  // sync effects update this ref; each skips when the value it would
  // write is already there.
  const syncedRef = useRef(null);

  // URL → context (direct nav wins on cold load and back/forward)
  useEffect(() => {
    if (routePid && routePid !== syncedRef.current) {
      syncedRef.current = routePid;
      selectPortfolio(routePid);
    }
  }, [routePid, selectPortfolio]);

  // Context → URL (strip picker, desk picker, quick-trade, etc.)
  useEffect(() => {
    if (activePid && activePid !== syncedRef.current) {
      syncedRef.current = activePid;
      nav(`/desk/${activePid}`, { replace: true });
    }
  }, [activePid, nav]);

  useEffect(() => {
    if (!activePid) return () => {};
    setEvents([]);
    setProposals([]);
    setSnapshots([]);
    setRisk(null);
    setSettings(null);
    const loadAll = async () => {
      try {
        const [s, r, e, p, snap] = await Promise.all([
          getSettings(activePid),
          getRisk(activePid),
          getEvents(activePid, 150),
          listProposals(activePid, { limit: 20 }),
          getSnapshots(activePid, 200),
        ]);
        setSettings(s.settings);
        setRisk(r);
        setEvents(e.items || []);
        setProposals(p.items || []);
        setSnapshots(snap.items || []);
      } catch (err) {
        /* 401/403 handled globally */
      }
      // Auto-pick up scanner-dispatched runs for the roster
      try {
        if (!manualBusy) {
          const runs = await listMyRuns();
          const live = (runs || []).find(
            (rr) => rr.portfolio_id === activePid && rr.status === "running",
          );
          if (live && (!run || run.run_id !== live.run_id)) {
            setRun({ ...live, transcript: [] });
            setRunning(true);
          }
        }
      } catch {
        /* silent */
      }
    };
    loadAll();
    deskPoll.current = setInterval(loadAll, POLL_MS);
    return () => {
      if (deskPoll.current) clearInterval(deskPoll.current);
    };
  }, [activePid, omsRefreshKey]);

  // Run polling: keep transcript live so roster lights up in sync
  useEffect(() => {
    if (!run?.run_id || run.status !== "running") {
      if (runPoll.current) clearInterval(runPoll.current);
      return () => {};
    }
    const poll = async () => {
      try {
        const r = await getAnalysis(run.run_id);
        setRun((prev) => {
          const prevLen = prev?.transcript?.length || 0;
          const nextLen = r?.transcript?.length || 0;
          if (nextLen > prevLen) {
            const newest = r.transcript[nextLen - 1];
            if (newest?.role && newest.role !== "system") {
              setSpeakingRole(newest.role);
              if (speakingTimer.current) clearTimeout(speakingTimer.current);
              speakingTimer.current = setTimeout(() => setSpeakingRole(null), 2500);
            }
          }
          return r;
        });
        if (r.status !== "running") {
          setRunning(false);
          if (run?.trigger === "manual") setManualBusy(false);
          setChartSymbol(r.symbol || chartSymbol);
          setOmsRefreshKey((k) => k + 1);
        }
      } catch {
        /* silent */
      }
    };
    poll();
    runPoll.current = setInterval(poll, POLL_MS);
    return () => {
      if (runPoll.current) clearInterval(runPoll.current);
    };
  }, [run?.run_id, run?.status]);

  // Whenever the currently displayed run changes symbol, sync the chart.
  useEffect(() => {
    if (run?.symbol && run.symbol !== chartSymbol) {
      setChartSymbol(run.symbol);
    }
  }, [run?.symbol]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleTickerSelect = useCallback(
    (sym) => {
      setChartSymbol(sym);
      // Keep the URL query in sync for shareable links + so refreshes retain
      // the picked symbol.
      const next = new URLSearchParams(searchParams);
      next.set("symbol", sym);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Respond to external URL changes (e.g. Discover → /desk?symbol=NVDA).
  useEffect(() => {
    const urlSym = searchParams.get("symbol");
    if (urlSym && urlSym !== chartSymbol) setChartSymbol(urlSym);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleApprove = async (p) => {
    setBusy(true);
    try {
      await approveProposal(activePid, p.id);
      toast.success(`Approved ${p.side} ${p.symbol}`);
      setOmsRefreshKey((k) => k + 1);
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (p) => {
    setBusy(true);
    try {
      await rejectProposal(activePid, p.id);
      toast.success(`Rejected ${p.side} ${p.symbol}`);
      setOmsRefreshKey((k) => k + 1);
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const activePortfolio = useMemo(
    () => portfolios.find((p) => p.id === activePid),
    [portfolios, activePid],
  );

  const refreshScannerStatus = useCallback(async () => {
    if (!activePid) return;
    try {
      const s = await getScanner(activePid);
      setScannerStatus(s);
    } catch {
      /* silent */
    }
  }, [activePid]);

  const autoMode = !!settings?.auto_mode;

  return (
    <div data-testid="desk-page" className="grid gap-3">
      {/* Top bar */}
      <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1fr)_auto]">
        <TickerBar onSelect={handleTickerSelect} />
        <div className="flex items-center justify-between gap-2 border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-3 py-1.5">
          <div className="flex items-center gap-2">
            <div className="aurion-section-title">Portfolio</div>
            {portfolios.length === 0 ? (
              <button
                data-testid="desk-create-first-portfolio"
                className="aurion-btn-gold px-3 py-1 text-[10px] uppercase tracking-widest"
                onClick={() => nav("/portfolios")}
              >
                + create one
              </button>
            ) : (
              <Select
                value={activePid || undefined}
                onValueChange={(v) => selectPortfolio(v)}
              >
                <SelectTrigger
                  data-testid="desk-portfolio-select"
                  className="h-7 min-w-[190px] border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[11px]"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] text-[var(--aurion-text)]">
                  {portfolios.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · ${Number(p.total_equity).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="desk-open-settings"
              onClick={() => setSettingsOpen(true)}
              disabled={!activePid}
              className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[var(--aurion-muted)] hover:text-[var(--aurion-gold)] disabled:opacity-50"
            >
              <Settings2 className="h-3 w-3" />
              <span
                data-testid="desk-automode-badge"
                className={autoMode ? "text-[var(--aurion-gold)]" : ""}
              >
                Auto · {autoMode ? "ON" : "OFF"}
              </span>
            </button>
            <AutopilotControl
              portfolioId={activePid}
              portfolioType={activePortfolio?.type}
              scannerStatus={scannerStatus}
              autoMode={autoMode}
              nowOverride={fxNowOverride}
              onChange={() => {
                refreshScannerStatus();
                setOmsRefreshKey((k) => k + 1);
              }}
            />
          </div>
        </div>
      </div>

      {/* 3-column grid — narrow roster ~210px · big chart · narrow right ~300px */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[210px_minmax(0,1fr)_300px]">
        {/* Left: roster (compact) */}
        <div className="min-h-[calc(100vh-200px)] border border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2.5">
          <AgentRoster run={run} speakingRole={speakingRole} />
        </div>

        {/* Center: LARGE chart + feed */}
        <div className="flex min-h-[calc(100vh-200px)] flex-col gap-3">
          <div className="h-[440px] border border-[var(--aurion-border)] bg-[var(--aurion-panel)]">
            <ChartPro
              symbol={chartSymbol}
              autoMode={autoMode}
              onTradeIntent={
                autoMode
                  ? null
                  : (intent) =>
                      setTradeIntent({ ...intent, portfolioId: activePid })
              }
            />
          </div>
          <div className="min-h-[280px] flex-1 border border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2.5">
            <DeskFeed events={events} />
          </div>
        </div>

        {/* Right: risk + scanner/watchlist tabs + approval + OMS */}
        <div className="flex min-h-[calc(100vh-200px)] flex-col gap-3">
          <div className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2.5">
            <RiskGauges risk={risk} />
          </div>
          <div className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)]">
            <div className="flex items-stretch border-b border-[var(--aurion-border)]">
              <TabButton
                testid="desk-tab-scanner"
                active={rightTab === "scanner"}
                onClick={() => setRightTab("scanner")}
                Icon={Radar}
              >
                Scanner
              </TabButton>
              <TabButton
                testid="desk-tab-watchlist"
                active={rightTab === "watchlist"}
                onClick={() => setRightTab("watchlist")}
                Icon={Star}
              >
                Watchlist
              </TabButton>
            </div>
            <div className="p-2.5">
              {rightTab === "scanner" ? (
                <ScannerCard
                  portfolioId={activePid}
                  portfolioType={activePortfolio?.type}
                  onStatusChange={setScannerStatus}
                />
              ) : (
                <WatchlistPanel
                  onSelect={handleTickerSelect}
                  activeSymbol={chartSymbol}
                />
              )}
            </div>
          </div>
          <div className="border border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2.5">
            <ApprovalQueue
              proposals={proposals}
              settings={settings}
              onApprove={handleApprove}
              onReject={handleReject}
              busy={busy}
            />
          </div>
          <div className="flex-1 border border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2.5">
            <OmsPanel
              portfolioId={activePid}
              refreshKey={omsRefreshKey}
              snapshots={snapshots}
              portfolio={activePortfolio}
            />
          </div>
        </div>
      </div>

      <DeskSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        portfolioId={activePid}
        onSaved={(s) => setSettings(s)}
      />

      <QuickTradeDialog
        open={!!tradeIntent}
        intent={tradeIntent}
        portfolio={activePortfolio}
        onClose={() => setTradeIntent(null)}
        onFilled={() => setOmsRefreshKey((k) => k + 1)}
      />
    </div>
  );
}

function TabButton({ children, active, onClick, testid, Icon }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 border-r border-[var(--aurion-border)] px-2 py-2 font-heading text-[10px] tracking-[0.18em] transition-colors last:border-r-0 ${
        active
          ? "bg-[var(--aurion-gold)]/10 text-[var(--aurion-gold)]"
          : "text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
      }`}
    >
      {Icon ? <Icon className="h-3 w-3" /> : null}
      <span>{children}</span>
    </button>
  );
}
